import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import Icon from '@src/basic-components/Icon/Icon';
import JoinButton from '@src/components/JoinButton/JoinButton';
import SocialShare, { type SocialShareProps } from '@src/components/SocialShare/SocialShare';
import TokenImage from '@src/components/Token/TokenImage/TokenImage';
import TokenMenuPopover from '@src/components/Token/TokenMenuPopover/TokenMenuPopover';
import TokenStats from '@src/components/Token/TokenStats';
import { useStoreState } from '@src/model/hooks';
import token from '@src/model/token';
import { getTokenShareUrl } from '@src/model/token/helpers';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { t } from 'i18next';
import { Fragment, memo } from 'react';
import type { FC } from 'react';
import { PostHero } from '../PostHero';

export interface PostsTabHeroProps {
    postId: string;
    tokenName: string;
    className?: string;
    fallbackImage?: string;
}

const PostsTabHero: FC<PostsTabHeroProps> = props => {
    const { tokenName, postId, className, fallbackImage } = props;

    const avatarSizeClasses = 'size-[64px] lg:size-[100px]';
    const spaceAfterAvatar = 'ml-3 lg:ml-4'; // Corresponds to theme.spacing.3 and theme.spacing.4
    const isAuthenticating = useStoreState(state => state.user.isAuthenticating);

    function renderMenuPopover() {
        return <TokenMenuPopover tokenName={tokenName} />;
    }

    function renderTokenStats() {
        return (
            <div className={`flex-1 ${spaceAfterAvatar}`}>
                <TokenStats tokenName={tokenName} />
            </div>
        );
    }

    function renderJoinButton() {
        if (isAuthenticating) return;
        return <JoinButton className="flex" buttonProps={{ size: 'xs', variant: 'ghost' }} tokenName={tokenName} />;
    }

    function renderShareButton() {
        if (!token) return null;

        const props: SocialShareProps = {
            useShortUrl: true,
            shareUrlPayload: {
                url: getTokenShareUrl(tokenName),
                title: token.inviteTitle,
                text: token.inviteText,
            },
            renderTrigger: ctx => (
                <div className="w-4">
                    <Icon name="share" className={`transition flex cursor-pointer ${ctx.isCreatingShortUrl ? 'opacity-50' : 'opacity-100'}`} />
                </div>
            ),
        };

        return <SocialShare {...props} className="flex-1" />;
    }

    return (
        <div className={className}>
            <PostHero postId={postId} tokenName={tokenName} />

            <div className={'flex px-4 pt-1 lg:pt-2 relative z-10'}>
                <TokenImage className={`flex-shrink-0 -mt-[48px] lg:-mt-[84px] ${avatarSizeClasses}`} tokenName={tokenName} />
                {renderTokenStats()}
                <div className="flex-1" />
                <div className="flex items-center space-x-2">
                    {renderJoinButton()}
                    {renderShareButton()}
                    {renderMenuPopover()}
                </div>
            </div>
        </div>
    );
};

export default memo(PostsTabHero, propsEqualityFn);
